'use client';

import { useRef, useEffect, useState } from 'react';
import { LocateFixed, Search } from 'lucide-react';

import {
  CompassContextType,
  useCompassContext,
} from '@/src/libs/CompassContext';
import Button from '@/src/components/UI/Button';
import {
  initializeMap,
  DEFAULT_CENTER,
} from '@/src/utils/map-utils/initializeMap';
import { setMarkerImage } from '@/src/utils/map-utils/buildMarker';
import {
  getMarkerAltText,
  getMarkerKind,
} from '@/src/utils/map-utils/markerKind';
import { getGeolocationCenter } from '@/src/utils/map-utils/getGeolocationCenter';
import { useFetchMeetings } from '@/src/hooks/useFetchMeetings';

import InfoMessage from '../UI/InfoMessage';

import InfoWindow from './InfoWindow';

const Map = (): JSX.Element => {
  const ref = useRef<HTMLDivElement>(null);
  const [geolocationNotice, setGeolocationNotice] = useState<string | null>(
    null
  );
  const [geolocationEnabled, setGeolocationEnabled] = useState<boolean>(false);
  const [initialLocationSet, setInitialLocationSet] = useState<boolean>(false);

  const ctx = useCompassContext();

  const { data, error, isLoading } = useFetchMeetings(ctx, initialLocationSet);

  useEffect(() => {
    if (!ctx) return;
    if (!ctx.state.map) {
      initializeMap(ctx, ref);
    }
  }, [ctx, ref]);

  useEffect(() => {
    if (!ctx || !ctx.state.map || initialLocationSet) return;

    // const getInitialLocation = async () => {
    //   try {
    //     await getGeolocationCenter(ctx);
    //     setGeolocationEnabled(true);
    //   } catch (geoError) {
    //     setGeolocationNotice((geoError as Error).message);
    //     setTimeout(() => {
    //       setGeolocationNotice(null);
    //     }, 3000);
    //     ctx.dispatch({
    //       type: 'SET_MAP_CENTER',
    //       payload: DEFAULT_CENTER,
    //     });
    //   } finally {
    //     setInitialLocationSet(true);
    //   }
    // };

    // getInitialLocation();

    ctx.dispatch({
      type: 'ACCEPT_MAP_CENTER',
      payload: DEFAULT_CENTER,
    });
    setInitialLocationSet(true);
  }, [ctx?.state?.map, initialLocationSet]);

  useEffect(() => {
    if (!ctx) return;
    if (ctx.state.map && initialLocationSet) {
      const idleListener = ctx.state.map.addListener('idle', () => {
        const currentCenter = {
          lat: ctx.state.map.getCenter()?.lat(),
          lng: ctx.state.map.getCenter()?.lng(),
        };

        const acceptedCenter = ctx.acceptedMapCenterRef.current;
        ctx.dispatch({
          type: 'SHOW_FETCH_BUTTON',
          payload:
            acceptedCenter?.lat !== currentCenter.lat ||
            acceptedCenter?.lng !== currentCenter.lng,
        });
      });

      return () => {
        google.maps.event.removeListener(idleListener);
      };
    }
  }, [ctx?.state.map, initialLocationSet]);

  useEffect(() => {
    if (!ctx) return;
    if (ctx.state.map && data) {
      data.find((meeting) => {
        setMarkerImage(
          meeting.marker,
          getMarkerKind(
            meeting.meetings.length,
            meeting.markerId === ctx.state.meetingFocus
          ),
          getMarkerAltText(meeting.meetings)
        );
      });
    }
  }, [ctx?.state?.meetingFocus, data, ctx?.state?.map]);

  const fetchMeetingsWithLocation = async (ctx: CompassContextType) => {
    try {
      await getGeolocationCenter(ctx);
      setGeolocationEnabled(true);
    } catch (geoError) {
      setGeolocationNotice((geoError as Error).message);
      setTimeout(() => {
        setGeolocationNotice(null);
      }, 3000);
    }
  };

  const handleFetchMeetings = async () => {
    if (!ctx) return;
    ctx.dispatch({
      type: 'ACCEPT_MAP_CENTER',
      payload: {
        lat: ctx.state.map.getCenter()?.lat(),
        lng: ctx.state.map.getCenter()?.lng(),
      },
    });
  };

  const handleGeolocate = async () => {
    if (!ctx) return;
    fetchMeetingsWithLocation(ctx);
  };

  return (
    <>
      <InfoWindow
        error={error}
        isLoading={isLoading}
        markers={data}
        initialLocationSet={initialLocationSet}
      />
      <div
        className="h-1/2 md:h-full relative md:order-1 md:w-[calc(100%-380px)]"
        data-testid="map"
        ref={ref}
      >
        {ctx?.state.map && geolocationEnabled && (
          <div className="absolute bottom-6 right-6 z-10">
            <Button
              ariaLabel="Locate me on map"
              onClick={handleGeolocate}
              icon={LocateFixed}
              title="Locate me"
            />
          </div>
        )}
        {!geolocationNotice && ctx?.state?.showFetchButton && (
          <div className="absolute top-6 w-full flex justify-center">
            <Button
              ariaLabel="Search this area"
              onClick={handleFetchMeetings}
              icon={Search}
              title="Search this area"
            />
          </div>
        )}
        {geolocationNotice && (
          <div className="absolute top-6 w-full flex justify-center">
            <InfoMessage message={geolocationNotice} />
          </div>
        )}
      </div>
    </>
  );
};

export default Map;
